/**
 * Pure mapping helpers for the per-dimension coverage panel (88 · Task 2.1).
 *
 * The panel is driven ONLY by the server's `dimensionCoverage` rows (a
 * founder-friendly `label` + a 3-state qualitative `state`). These helpers turn
 * each state into founder-facing display copy + a semantic tone the component
 * maps to theme colors. They are trade-secret-safe by construction: they never
 * see a number, weight, score, or sub-criterion count — only the 3-state marker.
 *
 * Kept standalone (no RN imports) so the mapping is unit-tested without the
 * component tree — matches `interview-exit.ts` / `interview-progress-bar.ts`.
 */

import type {
  DimensionCoverage,
  DimensionCoverageState,
} from '@/lib/interview/types';

/** Founder-facing display metadata for one coverage state. */
export interface CoverageChipMeta {
  /** Short, non-numeric status word shown on the chip. */
  statusLabel: string;
  /** Semantic tone the component maps to theme colors (never a raw color here). */
  tone: DimensionCoverageState;
}

/**
 * Map a 3-state coverage marker to founder-facing copy + tone. Deliberately
 * qualitative — no "score", no percentage, no count.
 */
export function coverageStateMeta(state: DimensionCoverageState): CoverageChipMeta {
  switch (state) {
    case 'captured':
      return { statusLabel: 'Captured', tone: 'captured' };
    case 'light':
      return { statusLabel: 'A little', tone: 'light' };
    case 'not_yet':
    default:
      return { statusLabel: 'Not yet', tone: 'not_yet' };
  }
}

/**
 * Keep only rows with a real dimension label — defends the panel against a
 * malformed/empty row rather than rendering a blank chip.
 */
export function visibleDimensionCoverage(
  items: ReadonlyArray<DimensionCoverage> | undefined | null,
): DimensionCoverage[] {
  if (!items) return [];
  return items.filter((item) => !!item?.label && item.label.trim().length > 0);
}
